/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  ThemeProvider,
  ImageList,
  ImageListItem,
  TextField,
  Select,
  MenuItem,
  IconButton,
  FormControlLabel,
  Switch,
  Slide,
  Collapse,
  Card,
  CardMedia,
  CardContent,
  InputAdornment,
  Grid, // Import Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Close as CloseIcon, // Import CloseIcon
} from '@mui/icons-material';
import axiosInstance from '../../api/axiosConfig';
import theme from '../../theme';
import PropTypes from 'prop-types';

// Revised Harmonious Color Palette (No changes)
const harmoniousColors = {
  primary: '#10B981',
  primaryLight: '#34D399',
  primaryDark: '#059669',
  secondary: '#A1887F',
  secondaryLight: '#D2B48C',
  secondaryDark: '#795548',
  accent: '#FFCA28',
  accentLight: '#FFD54F',
  accentDark: '#FFC107',
  background: '#F5F5F5',
  paper: '#FFFFFF',
  divider: '#E0E0E0',
  textPrimary: '#212121',
  textSecondary: '#757575',
  success: '#4CAF50',
  successLight: '#81C784',
  error: '#F44336',
  errorLight: '#E57373',
  warning: '#FF9800',
  warningLight: '#FFB74D',
};

// Utility Functions (No changes)
const formatTitle = (str) => {
  if (!str) return '';
  return str
    .split(/[._ -]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};
const isDropdownField = (obj) =>
  obj && typeof obj === 'object' && 'dropdownOptions' in obj && Array.isArray(obj.dropdownOptions);
const mightBeImageField = (label, val) => {
  if (label && /image|img|logo/i.test(label)) return true;
  if (!val || typeof val !== 'string') return false;
  return /^(https?:\/\/|www\.)/i.test(val.trim());
};
const isDateField = (label, val) => {
  if (typeof val !== 'string') return false;
  if (!/date|day/i.test(label)) return false;
  return /^\d{4}-\d{2}-\d{2}$/.test(val.trim());
};
const isColor = (str) => typeof str === 'string' && /^#[0-9A-Fa-f]{6}$/.test(str.trim());

// Data Manipulation Functions (No changes)
const updateDataAtPath = (obj, path, newVal) => {
  if (!path.length) return newVal;
  const [key, ...rest] = path;
  if (Array.isArray(obj)) {
    const copy = [...obj];
    copy[key] = updateDataAtPath(copy[key], rest, newVal);
    return copy;
  }
  return { ...obj, [key]: updateDataAtPath(obj[key], rest, newVal) };
};
const deleteArrayItem = (obj, path) => {
  const index = path[path.length - 1];
  const parentPath = path.slice(0, -1);
  const parent = parentPath.reduce((acc, k) => acc?.[k], obj);
  if (!Array.isArray(parent)) return obj;
  const updated = parent.filter((_, i) => i !== index);
  return updateDataAtPath(obj, parentPath, updated);
};
const addArrayItem = (obj, path) => {
  const arr = path.reduce((acc, k) => acc?.[k], obj);
  if (!Array.isArray(arr)) return obj;
  let newItem = '';
  if (arr.length > 0 && typeof arr[0] === 'object' && arr[0] !== null) {
    newItem = {};
    Object.keys(arr[0]).forEach((k) => { newItem[k] = ''; });
  }
  const newArr = [...arr, newItem];
  return updateDataAtPath(obj, path, newArr);
};
const diffFields = (oldObj, newObj, path = []) => {
  let changes = [];
  if (typeof oldObj !== 'object' || typeof newObj !== 'object' || !oldObj || !newObj) {
    if (oldObj !== newObj) changes.push({ path, oldVal: oldObj, newVal: newObj });
    return changes;
  }
  if (Array.isArray(oldObj) && Array.isArray(newObj)) {
    const len = Math.max(oldObj.length, newObj.length);
    for (let i = 0; i < len; i++) {
      if (i >= oldObj.length) {
        changes.push({ path: [...path, i], oldVal: undefined, newVal: newObj[i] });
      } else if (i >= newObj.length) {
        changes.push({ path: [...path, i], oldVal: oldObj[i], newVal: undefined });
      } else {
        changes = changes.concat(diffFields(oldObj[i], newObj[i], [...path, i]));
      }
    }
    return changes;
  }
  if (!Array.isArray(oldObj) && !Array.isArray(newObj)) {
    const keys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);
    keys.forEach((k) => {
      const o = oldObj[k];
      const n = newObj[k];
      if (o !== undefined && n === undefined) {
        changes.push({ path: [...path, k], oldVal: o, newVal: undefined });
      } else if (o === undefined && n !== undefined) {
        changes.push({ path: [...path, k], oldVal: undefined, newVal: n });
      } else if (o !== n) {
        changes = changes.concat(diffFields(o, n, [...path, k]));
      }
    });
    return changes;
  }
  if (oldObj !== newObj) changes.push({ path, oldVal: oldObj, newVal: newObj });
  return changes;
};
const diffDataFields = (oldData, newData) => diffFields(oldData, newData, []);


// ImageSelectorDialog Component
const ImageSelectorDialog = memo(ImageSelectorDialogMemo);
function ImageSelectorDialogMemo(props) { return <ImageSelectorDialogBase {...props} harmoniousColors={harmoniousColors} axiosInstance={axiosInstance} formatTitle={formatTitle} ImageList={ImageList} ImageListItem={ImageListItem} ImageSelectorDialog={ImageSelectorDialog} Box={Box} Typography={Typography} Button={Button} CircularProgress={CircularProgress} Dialog={Dialog} DialogTitle={DialogTitle} DialogContent={DialogContent} DialogActions={DialogActions} Snackbar={Snackbar} ThemeProvider={ThemeProvider} Slide={Slide} Collapse={Collapse} Card={Card} CardMedia={CardMedia} CardContent={CardContent} InputAdornment={InputAdornment} AddIcon={AddIcon} DeleteIcon={DeleteIcon} ExpandMoreIcon={ExpandMoreIcon} ExpandLessIcon={ExpandLessIcon} SearchIcon={SearchIcon} ClearIcon={ClearIcon} MenuItem={MenuItem} Select={Select} TextField={TextField} FormControlLabel={FormControlLabel} Switch={Switch} IconButton={IconButton} theme={theme} useState={useState} useEffect={useEffect} useCallback={useCallback} memo={memo} ref={props.ref} />; }
function ImageSelectorDialogBase({
  open,
  onClose,
  images = [],
  loading,
  onSelect,
  onUploadComplete,
  subdomain,
  harmoniousColors,
  axiosInstance,
  Box, Typography, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, ImageList, ImageListItem, Card, CardMedia, CardContent, InputAdornment, AddIcon, DeleteIcon, ExpandMoreIcon, ExpandLessIcon, SearchIcon, ClearIcon, MenuItem, Select, TextField, FormControlLabel, Switch, IconButton, useState, useEffect, useCallback, memo, theme
}) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const schoolId = localStorage.getItem("schoolId");
  const [previewImageUrl, setPreviewImageUrl] = useState(null);

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) setFile(e.target.files[0]);
    setPreviewImageUrl(null);
  };

  const handleUpload = async () => {
    if (!file) return;
    try {
      setUploading(true);
      const timestamp = Date.now();
      const autoTitle = `web_image_${schoolId}_${timestamp}`;
      const imageUrl = await axiosInstance.uploadImage(
        file,
        'IMAGES',
        schoolId,
        autoTitle,
        'Web Assets'
      );
      onSelect(imageUrl);
      onUploadComplete && onUploadComplete();
      setFile(null);
      onClose();
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload image: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleImageItemClick = (imageUrl) => {
    setPreviewImageUrl(imageUrl);
  };

  const handleSelectButtonClick = () => {
    if (previewImageUrl) {
      onSelect(previewImageUrl);
      onClose();
    } else {
      alert("Please select an image or upload one.");
    }
  };


  return (
    <Dialog open={open} onClose={() => { setFile(null); setPreviewImageUrl(null); onClose(); }} maxWidth="md" fullWidth TransitionComponent={Slide} transitionDuration={300}>
      <DialogTitle sx={{ fontSize: '1.1rem', fontWeight: 'bold', bgcolor: harmoniousColors.primary, color: '#fff', py: 1.5, px: 2 }}>
        Select / Upload Image
      </DialogTitle>
      <DialogContent sx={{ p: 2, bgcolor: 'background.paper' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.8, fontSize: '0.9rem' }}>
          Existing Images
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
            <CircularProgress size={20} />
          </Box>
        ) : images?.length ? (
          <Box sx={{ height: 180, overflowY: 'auto', mb: 1 }}>
            <ImageList cols={4} gap={6}>
              {images.map((img) => (
                <ImageListItem
                  key={img.id}
                  sx={{
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    ':hover': { transform: 'scale(1.05)' },
                    opacity: previewImageUrl === img.fileUrl ? 0.6 : 1,
                    border: previewImageUrl === img.fileUrl ? `2px solid ${harmoniousColors.primary}` : 'none',
                    borderRadius: 1,
                  }}
                  onClick={() => handleImageItemClick(img.fileUrl)}
                >
                  <img
                    src={img.fileUrl}
                    alt={img.eventName}
                    style={{ borderRadius: 4, width: '100%', height: 'auto', objectFit: 'cover' }}
                  />
                </ImageListItem>
              ))}
            </ImageList>
          </Box>
        ) : (
          <Typography variant="caption" sx={{ fontStyle: 'italic', fontSize: '0.8rem' }}>
            (No images found - upload below)
          </Typography>
        )}

        {previewImageUrl && (
          <Card sx={{ mb: 1, border: `1px solid ${harmoniousColors.divider}` }}>
            <CardMedia
              component="img"
              image={previewImageUrl}
              alt="Image Preview"
              sx={{ maxHeight: 180, objectFit: 'contain', bgcolor: '#f0f0f0' }}
            />
            <CardContent sx={{ p: 1 }}>
              <Typography variant="subtitle2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>Image Preview</Typography>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: harmoniousColors.textSecondary, wordBreak: 'break-all' }}>
                URL: {previewImageUrl}
              </Typography>
            </CardContent>
          </Card>
        )}

        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.8, fontSize: '0.9rem' }}>
            Upload New Image
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Button variant="outlined" size="small" component="label" sx={{ fontSize: '0.8rem', px: 1 }}>
              Choose File
              <input hidden type="file" accept="image/*" onChange={handleFileChange} />
            </Button>
            {file && (
              <Typography variant="body2" sx={{ bgcolor: '#eee', p: 0.5, borderRadius: 1, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                {file.name}
              </Typography>
            )}
            <Button variant="contained" size="small" disabled={!file || uploading} onClick={handleUpload} sx={{ fontSize: '0.8rem', px: 1 }}>
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </Box>
          {file && (
            <Box sx={{ mt: 1, border: `1px solid ${harmoniousColors.divider}`, borderRadius: 1, overflow: 'hidden' }}>
              <CardMedia
                component="img"
                image={file ? URL.createObjectURL(file) : ''}
                alt="Upload Preview"
                sx={{ maxHeight: 130, objectFit: 'contain', bgcolor: '#f0f0f0' }}
              />
              <CardContent sx={{ p: 1 }}>
                <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>Upload Preview</Typography>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: harmoniousColors.textSecondary }}>
                  File name: {file.name}, Size: {(file.size / 1024).toFixed(1)} KB
                </Typography>
              </CardContent>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button onClick={() => { setFile(null); setPreviewImageUrl(null); onClose(); }} size="small" sx={{ fontSize: '0.8rem', px: 1 }}>
          Close
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleSelectButtonClick}
          disabled={!previewImageUrl}
          sx={{ fontSize: '0.8rem', px: 1 }}
        >
          Select
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ImageSelectorDialogMemo.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  images: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onUploadComplete: PropTypes.func,
  subdomain: PropTypes.string.isRequired,
};


// Field Renderer Components
const DropdownField = memo(DropdownFieldMemo);
function DropdownFieldMemo(props) { return <DropdownFieldBase {...props} harmoniousColors={harmoniousColors} formatTitle={formatTitle} Box={Box} Typography={Typography} Select={Select} MenuItem={MenuItem} />; }
function DropdownFieldBase({ label, fieldData, path, onChange, highlighted, harmoniousColors, formatTitle, Box, Typography, Select, MenuItem }) {
  const handleSelectChange = (e) => onChange(path, { ...fieldData, value: e.target.value });
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: harmoniousColors.textPrimary, fontSize: '0.85rem', ...(highlighted && { backgroundColor: harmoniousColors.accentLight, padding: '2px 4px', borderRadius: '4px' }) }}>
        {formatTitle(label)}
      </Typography>
      <Select size="small" value={fieldData.value ?? ''} onChange={handleSelectChange} fullWidth sx={{ fontSize: '0.85rem' }}>
        {fieldData.dropdownOptions.map((opt) => (
          <MenuItem key={opt} value={opt} sx={{ fontSize: '0.85rem' }}>{opt}</MenuItem>
        ))}
      </Select>
    </Box>
  );
}
DropdownFieldBase.propTypes = {
  label: PropTypes.string,
  fieldData: PropTypes.object,
  path: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  onChange: PropTypes.func,
  highlighted: PropTypes.bool,
  harmoniousColors: PropTypes.object,
  formatTitle: PropTypes.func,
  Box: PropTypes.elementType,
  Typography: PropTypes.elementType,
  Select: PropTypes.elementType,
  MenuItem: PropTypes.elementType,
};


const PrimitiveField = memo(PrimitiveFieldMemo);
function PrimitiveFieldMemo(props) { return <PrimitiveFieldBase {...props} harmoniousColors={harmoniousColors} formatTitle={formatTitle} isDateField={isDateField} isColor={isColor} mightBeImageField={mightBeImageField} Box={Box} Typography={Typography} TextField={TextField} Card={Card} CardMedia={CardMedia} CardContent={CardContent} Button={Button} />; }
function PrimitiveFieldBase({ label, value, path, onChange, onOpenImageDialog, highlighted, harmoniousColors, formatTitle, isDateField, isColor, mightBeImageField, Box, Typography, TextField, Card, CardMedia, CardContent, Button }) {
  const handleChange = (e) => {
    if (typeof value === 'boolean') {
      onChange(path, e.target.checked);
    } else {
      onChange(path, e.target.value);
    }
  };

  const dateField = isDateField(label, value);
  const colorField = isColor(value);
  const pickImage = mightBeImageField(label, value);
  const showPreview = pickImage && value;
  const displayValue = typeof value === 'boolean' ? undefined : value;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: harmoniousColors.textPrimary, fontSize: '0.85rem', ...(highlighted && { backgroundColor: harmoniousColors.accentLight, padding: '2px 4px', borderRadius: '4px' }) }}>
        {formatTitle(label)}
      </Typography>
      {showPreview && (
        <Card sx={{ mb: 0.8, maxWidth: 180, border: `1px solid harmoniousColors.divider` }}>
          <CardMedia
            component="img"
            image={value}
            alt="Image Preview"
            sx={{ maxHeight: 90, objectFit: 'contain', bgcolor: '#f0f0f0' }}
            onError={(e) => { e.target.src = ''; }}
          />
          <CardContent sx={{ p: 0.8, display: 'flex', justifyContent: 'center' }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: harmoniousColors.textSecondary, fontStyle: 'italic' }}>
              Preview
            </Typography>
          </CardContent>
        </Card>
      )}
      {dateField ? (
        <TextField type="date" variant="outlined" size="small" value={value ?? ''} onChange={handleChange} fullWidth color="primary" sx={{ fontSize: '0.85rem' }} />
      ) : (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField variant="outlined" size="small" multiline maxRows={3} value={displayValue ?? ''} placeholder={typeof value === 'boolean' ? (value ? 'true' : 'false') : ''} onChange={handleChange} sx={{ flex: 1, fontSize: '0.85rem' }}  />
          {colorField && (
            <Box sx={{ width: 20, height: 20, mt: 0.3 }}>
              <input
                type="color"
                value={value}
                onChange={(e) => onChange(path, e.target.value)}
                style={{ width: '100%', height: '100%', cursor: 'pointer', border: 'none', background: 'transparent' }}
              />
            </Box>
          )}
          {pickImage && onOpenImageDialog && (
            <Button variant="contained" size="small" onClick={() => onOpenImageDialog(path)} color="primary" sx={{ fontSize: '0.75rem', px: 1 }}>
              Pick Image
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
PrimitiveFieldBase.propTypes = {
  label: PropTypes.string,
  value: PropTypes.any,
  path: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  onChange: PropTypes.func,
  onOpenImageDialog: PropTypes.func,
  highlighted: PropTypes.bool,
  harmoniousColors: PropTypes.object,
  formatTitle: PropTypes.func,
  isDateField: PropTypes.func,
  isColor: PropTypes.func,
  mightBeImageField: PropTypes.func,
  Box: PropTypes.elementType,
  Typography: PropTypes.elementType,
  TextField: PropTypes.elementType,
  Card: PropTypes.elementType,
  CardMedia: PropTypes.elementType,
  CardContent: PropTypes.elementType,
  Button: PropTypes.elementType,
};


const FieldRenderer = memo(FieldRendererMemo);
function FieldRendererMemo(props) { return <FieldRendererBase {...props} harmoniousColors={harmoniousColors} formatTitle={formatTitle} isDropdownField={isDropdownField} Array={Array} Object={Object} Box={Box} Typography={Typography} IconButton={IconButton} ExpandLessIcon={ExpandLessIcon} ExpandMoreIcon={ExpandMoreIcon} Collapse={Collapse} AddIcon={AddIcon} DeleteIcon={DeleteIcon} Button={Button} PrimitiveField={PrimitiveField} DropdownField={DropdownField} />; }
function FieldRendererBase({ label, value, path, onChange, onOpenImageDialog, level = 0, expandedKey, setExpandedKey, searchTerm, searchResultsPaths, harmoniousColors, formatTitle, isDropdownField, Array, Object, Box, Typography, IconButton, ExpandLessIcon, ExpandMoreIcon, Collapse, AddIcon, DeleteIcon, Button, PrimitiveField, DropdownField }) {
  const indent = level * 15;
  const isRootLevel = level === 0;
  const isExpanded = isRootLevel ? expandedKey === label : true;
  const hasChildren = (value && typeof value === 'object' && Object.keys(value).length > 0) || (Array.isArray(value) && value.length > 0);
  const [dialogOpen, setDialogOpen] = useState(false);

  const toggleExpand = () => {
    if (isRootLevel && hasChildren) {
      setDialogOpen(true);
    } else {
      setExpandedKey(expandedKey === label ? null : label); // Keep collapse for nested levels if needed
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleDialogSave = (dialogData) => {
    onChange(path, dialogData);
    setDialogOpen(false);
  };


  const currentPathString = path.join('.');
  const isHighlighted = searchResultsPaths.has(currentPathString);

  const highlightStyle = isHighlighted ? { backgroundColor: harmoniousColors.accentLight, padding: '2px 4px', borderRadius: '4px' } : {};

  if (isDropdownField(value)) {
    return <DropdownField label={label} fieldData={value} path={path} onChange={onChange} highlighted={isHighlighted} />;
  }

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const keys = Object.keys(value);
    return (
      <Box sx={{ mb: 1.5, pl: indent, borderLeft: `1px solid ${harmoniousColors.divider}`, paddingLeft: 1, ...(isRootLevel && { borderLeft: 'none', paddingLeft: 0, mb: 0 }) }}>
        {label && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, ...(isRootLevel && { mb: 0 }) }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mr: 0.8, color: harmoniousColors.textPrimary, fontSize: '0.9rem', ...highlightStyle }}>
              {formatTitle(label)}
            </Typography>
            {isRootLevel && hasChildren && (
              <IconButton size="small" onClick={toggleExpand} sx={{ ml: -0.3 }}>
                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            )}
          </Box>
        )}
        {isExpanded && !isRootLevel && ( // Only collapse for nested levels now
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ ...(isRootLevel && { pl: 1, borderLeft: `1px solid ${harmoniousColors.divider}`, paddingLeft: 1, mt: 0.8 }) }}>
              {keys.length === 0 ? (
                <Typography variant="caption" sx={{ fontStyle: 'italic', color: harmoniousColors.textSecondary, fontSize: '0.8rem' }}>(Empty object)</Typography>
              ) : (
                keys.map((subKey) => (
                  <FieldRenderer
                    key={subKey}
                    label={subKey}
                    value={value[subKey]}
                    path={[...path, subKey]}
                    onChange={onChange}
                    onOpenImageDialog={onOpenImageDialog}
                    level={level + 1}
                    expandedKey={expandedKey}
                    setExpandedKey={setExpandedKey}
                    searchTerm={searchTerm}
                    searchResultsPaths={searchResultsPaths}
                  />
                ))
              )}
            </Box>
          </Collapse>
        )}
        {!isRootLevel && !isExpanded && hasChildren && ( // Render collapsed state for nested objects
          <Box sx={{pl: 1}}>
            <Typography variant="caption" sx={{ fontStyle: 'italic', color: harmoniousColors.textSecondary, fontSize: '0.8rem', cursor: 'pointer' }} onClick={toggleExpand}>
              (Click to expand)
            </Typography>
          </Box>
        )}
         {isRootLevel && hasChildren && (
          <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ fontSize: '1rem', fontWeight: 'bold' }}>Edit {formatTitle(label)}</DialogTitle>
            <DialogContent>
              {keys.length === 0 ? (
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>(Empty object)</Typography>
              ) : (
                keys.map((subKey) => (
                  <FieldRenderer
                    key={subKey}
                    label={subKey}
                    value={value[subKey]}
                    path={[...path, subKey]}
                    onChange={onChange}
                    onOpenImageDialog={onOpenImageDialog}
                    level={level + 1}
                    expandedKey={expandedKey}
                    setExpandedKey={setExpandedKey}
                    searchTerm={searchTerm}
                    searchResultsPaths={searchResultsPaths}
                  />
                ))
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDialogClose} size="small">Cancel</Button>
              <Button onClick={() => handleDialogSave(value)} variant="contained" size="small">Save</Button>
            </DialogActions>
          </Dialog>
        )}
      </Box>
    );
  }


  if (Array.isArray(value)) {
    return (
      <Box sx={{ mb: 1.5, pl: indent, borderLeft: `1px solid ${harmoniousColors.divider}`, paddingLeft: 1, bgcolor: harmoniousColors.background }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, pt:0.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: harmoniousColors.textPrimary, fontSize: '0.9rem', ...highlightStyle }}>
            {formatTitle(label)}
          </Typography>
          <IconButton size="small" onClick={() => { onChange(path, 'ADD_ITEM'); }} sx={{ mr: -0.3 }}>
            <AddIcon fontSize="small" sx={{ color: harmoniousColors.accentDark }} />
          </IconButton>
        </Box>
        {value.length === 0 ? (
          <Typography variant="caption" sx={{ fontStyle: 'italic', color: harmoniousColors.textSecondary, fontSize: '0.8rem' }}>(Empty array)</Typography>
        ) : (
          value.map((item, idx) => (
            <Box key={idx} sx={{ mb: 0.8, p: 1, bgcolor: harmoniousColors.paper, borderRadius: 1, position: 'relative', border: `1px solid ${harmoniousColors.divider}` }}>
              {value.length > 1 && (
                <IconButton size="small" sx={{ position: 'absolute', top: 4, right: 4, color: harmoniousColors.error }} onClick={() => { if (window.confirm('Are you sure you want to delete this item?')) onChange(path, { type: 'DELETE_ITEM', index: idx }); }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
              {typeof item === 'object' && item !== null ? (
                Object.keys(item).map((k) => (
                  <FieldRenderer key={k} label={k} value={item[k]} path={[...path, idx, k]} onChange={onChange} onOpenImageDialog={onOpenImageDialog} level={level + 1} expandedKey={expandedKey} setExpandedKey={setExpandedKey} searchTerm={searchTerm} searchResultsPaths={searchResultsPaths} />
                ))
              ) : (
                <PrimitiveField label={`${formatTitle(label)} #${idx + 1}`} value={item} path={[...path, idx]} onChange={onChange} onOpenImageDialog={onOpenImageDialog} highlighted={isHighlighted} />
              )}
            </Box>
          ))
        )}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Button
            startIcon={<AddIcon />}
            size="small"
            onClick={() => onChange(path, 'ADD_ITEM')}
            sx={{ fontSize: '0.75rem', mb: 1.5, px: 1.5 }}
          >
            Add New Item
          </Button>
        </Box>
           {isRootLevel && hasChildren && (
              <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth="md">
                <DialogTitle sx={{ fontSize: '1rem', fontWeight: 'bold' }}>Edit {formatTitle(label)}</DialogTitle>
                <DialogContent>
                  {value.length === 0 ? (
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>(Empty array)</Typography>
                  ) : (
                    value.map((item, idx) => (
                      <Box key={idx} sx={{ mb: 0.8, p: 1, bgcolor: harmoniousColors.paper, borderRadius: 1, position: 'relative', border: `1px solid ${harmoniousColors.divider}` }}>
                        {value.length > 1 && (
                          <IconButton size="small" sx={{ position: 'absolute', top: 4, right: 4, color: harmoniousColors.error }} onClick={() => { if (window.confirm('Are you sure you want to delete this item?')) onChange(path, { type: 'DELETE_ITEM', index: idx }); }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                        {typeof item === 'object' && item !== null ? (
                          Object.keys(item).map((k) => (
                            <FieldRenderer key={k} label={k} value={item[k]} path={[...path, idx, k]} onChange={onChange} onOpenImageDialog={onOpenImageDialog} level={level + 1} expandedKey={expandedKey} setExpandedKey={setExpandedKey} searchTerm={searchTerm} searchResultsPaths={searchResultsPaths} />
                          ))
                        ) : (
                          <PrimitiveField label={`${formatTitle(label)} #${idx + 1}`} value={item} path={[...path, idx]} onChange={onChange} onOpenImageDialog={onOpenImageDialog} highlighted={isHighlighted} />
                        )}
                      </Box>
                    ))
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleDialogClose} size="small">Cancel</Button>
                  <Button onClick={() => handleDialogSave(value)} variant="contained" size="small">Save</Button>
                </DialogActions>
              </Dialog>
            )}
      </Box>
    );
  }
  return <PrimitiveField label={label} value={value} path={path} onChange={onChange} onOpenImageDialog={onOpenImageDialog} highlighted={isHighlighted} />;
}
FieldRendererBase.propTypes = {
  label: PropTypes.string,
  value: PropTypes.any,
  path: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  onChange: PropTypes.func,
  onOpenImageDialog: PropTypes.func,
  level: PropTypes.number,
  expandedKey: PropTypes.string,
  setExpandedKey: PropTypes.func,
  searchTerm: PropTypes.string,
  searchResultsPaths: PropTypes.object, // PropTypes.instanceOf(Set),
  harmoniousColors: PropTypes.object,
  formatTitle: PropTypes.func,
  isDropdownField: PropTypes.func,
  Array: PropTypes.any, // PropTypes.elementType,  // actually global object, not component
  Object: PropTypes.any, // PropTypes.elementType, // actually global object, not component
  Box: PropTypes.elementType,
  Typography: PropTypes.elementType,
  IconButton: PropTypes.elementType,
  ExpandLessIcon: PropTypes.elementType,
  ExpandMoreIcon: PropTypes.elementType,
  Collapse: PropTypes.elementType,
  AddIcon: PropTypes.elementType,
  DeleteIcon: PropTypes.elementType,
  Button: PropTypes.elementType,
  PrimitiveField: PropTypes.elementType,
  DropdownField: PropTypes.elementType,
};


const RainbowFunEditor = memo(RainbowFunEditorMemo);
function RainbowFunEditorMemo(props) { return <RainbowFunEditorBase {...props} harmoniousColors={harmoniousColors} formatTitle={formatTitle} diffDataFields={diffDataFields} addArrayItem={addArrayItem} deleteArrayItem={deleteArrayItem} updateDataAtPath={updateDataAtPath} axiosInstance={axiosInstance} theme={theme} Box={Box} Typography={Typography} Button={Button} CircularProgress={CircularProgress} Dialog={Dialog} DialogTitle={DialogTitle} DialogContent={DialogContent} DialogActions={DialogActions} Snackbar={Snackbar} ThemeProvider={ThemeProvider} Slide={Slide} Collapse={Collapse} Card={Card} CardMedia={CardMedia} CardContent={CardContent} InputAdornment={InputAdornment} AddIcon={AddIcon} DeleteIcon={DeleteIcon} ExpandMoreIcon={ExpandMoreIcon} ExpandLessIcon={ExpandLessIcon} SearchIcon={SearchIcon} ClearIcon={ClearIcon} MenuItem={MenuItem} Select={Select} TextField={TextField} FormControlLabel={FormControlLabel} Switch={Switch} IconButton={IconButton} FieldRenderer={FieldRenderer} ImageSelectorDialog={ImageSelectorDialog} CloseIcon={CloseIcon} Grid={Grid} />; }
function RainbowFunEditorBase({
  open = false,
  onClose = () => {},
  pageSlug = '',
  componentKey = '',
  onSave = () => {},
  subdomain = '',
  harmoniousColors,
  formatTitle,
  diffDataFields,
  addArrayItem,
  deleteArrayItem,
  updateDataAtPath,
  axiosInstance,
  theme,
  Box, Typography, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, ThemeProvider, Slide, Collapse, Card, CardMedia, CardContent, InputAdornment, AddIcon, DeleteIcon, ExpandMoreIcon, ExpandLessIcon, SearchIcon, ClearIcon, MenuItem, Select, TextField, FormControlLabel, Switch, IconButton, FieldRenderer, ImageSelectorDialog, CloseIcon, Grid
}) {
  const [pageData, setPageData] = useState({});
  const [initialDataState, setInitialDataState] = useState({});
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [changesList, setChangesList] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarText, setSnackbarText] = useState('');
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageFieldPath, setImageFieldPath] = useState([]);
  const [images, setImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState(true);

  // New Search States (No changes)
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResultsPaths, setSearchResultsPaths] = useState(new Set());

  // Search Functionality (No changes)
  const handleSearch = useCallback((term, currentData = pageData) => {
    setSearchTerm(term);
    if (!term) {
      setSearchResultsPaths(new Set());
      return;
    }

    const results = new Set();
    function searchRecursive(obj, path = []) {
      if (obj && typeof obj === 'object') {
        for (const key in obj) {
          const currentPath = [...path, key];
          if (String(key).toLowerCase().includes(term.toLowerCase())) {
            results.add(currentPath.slice(0, -1).join('.'));
          }
          if (typeof obj[key] === 'string' && String(obj[key]).toLowerCase().includes(term.toLowerCase())) {
            results.add(currentPath.join('.'));
          }
          searchRecursive(obj[key], currentPath);
        }
      } else if (typeof obj === 'string' && String(obj).toLowerCase().includes(term.toLowerCase())) {
        results.add(path.join('.'));
      }
    }
    searchRecursive(currentData);
    setSearchResultsPaths(results);
  }, [pageData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleFieldChange = useCallback((path, action) => {
    let newData = { ...pageData };
    if (action === 'ADD_ITEM') {
      newData = addArrayItem(newData, path);
    } else if (typeof action === 'object' && action.type === 'DELETE_ITEM') {
      newData = deleteArrayItem(newData, [...path, action.index]);
    } else {
      newData = updateDataAtPath(newData, path, action);
    }
    setPageData(newData);
    if (searchTerm) {
      handleSearch(searchTerm, newData);
    }
  }, [pageData, searchTerm, handleSearch]);


  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        setIsLoadingContent(true);
        try {
          const response = await axiosInstance.get(`/api/${subdomain}/pages/${pageSlug}`);
          const parsedContent = response.data.content ? JSON.parse(response.data.content) : {};
          setPageData(parsedContent);
          setInitialDataState(parsedContent);
        } catch (err) {
          console.error("Error fetching page data:", err);
          setSnackbarText(`Error loading page data: ${err.message}`);
          setSnackbarOpen(true);
        } finally {
          setIsLoadingContent(false);
        }
      };
      fetchData();
    }
  }, [open, pageSlug, subdomain]);


  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSaveClick = useCallback(() => {
    const diffs = diffDataFields(initialDataState, pageData);
    if (!diffs.length) {
      setSnackbarText('No changes detected.');
      setSnackbarOpen(true);
      return;
    }
    setChangesList(diffs);
    setConfirmSaveOpen(true);
  }, [initialDataState, pageData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const confirmSave = useCallback(async () => {
    try {
      const payload = {
        pageSlug: pageSlug,
        componentKey: componentKey,
        content: JSON.stringify(pageData),
        schoolId: localStorage.getItem('schoolId'),
      };

      const response = await axiosInstance.post(`/api/${localStorage.getItem("schoolId")}/pages/${pageSlug}`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.status === 200) {
        setInitialDataState(pageData);
        onSave(pageData);
        setSnackbarText('Page saved successfully!');
        setSnackbarOpen(true);
        onClose();
      } else {
        throw new Error('Failed to save data');
      }
    } catch (err) {
      console.error('Save error:', err);
      setSnackbarText(`Error saving data: ${err.message}`);
      setSnackbarOpen(true);
    } finally {
      setConfirmSaveOpen(false);
    }
  }, [onClose, onSave, pageData, pageSlug, componentKey, subdomain]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const openImageDialog = useCallback(async (path) => {
    setImageFieldPath(path);
    setImageDialogOpen(true);
    try {
      setLoadingImages(true);
      const schoolId=localStorage.getItem("schoolId");
      const resp = await axiosInstance.get('/api/images/all', { headers: { schoolId } });
      setImages(resp.data || []);
    } catch (err) {
      console.error('Error fetching images:', err);
      setSnackbarText(`Error fetching images: ${err.message}`);
      setSnackbarOpen(true);
    } finally {
      setLoadingImages(false);
    }
  }, [subdomain]);

  const handleSelectImage = useCallback((url) => {
    handleFieldChange(imageFieldPath, url);
  }, [handleFieldChange, imageFieldPath]);

  const handleUploadComplete = useCallback(() => {
    openImageDialog(imageFieldPath);
  }, [imageFieldPath, openImageDialog]);


  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchResultsPaths(new Set());
  };


  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        TransitionComponent={Slide}
        transitionDuration={300}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '6px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 6px 24px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2, pt: 2, pl: 2, pr: 1, borderBottom: `1px solid ${harmoniousColors.divider}` }}>
          <Typography variant="h6" fontWeight={600} sx={{ color: harmoniousColors.textPrimary, fontSize: '1rem' }}>Edit Configuration</Typography>
          <IconButton onClick={onClose} aria-label="close" size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, bgcolor: theme.palette.background.editor, }}>
          <TextField
            size="small"
            placeholder="Search settings..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton aria-label="clear search" onClick={handleClearSearch} edge="end" size="small">
                    <ClearIcon color="action" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2, bgcolor: 'white', borderRadius: '6px', '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.4 } }}
          />
          {isLoadingContent ? (
            <Box sx={{ minHeight: '300px', p: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <CircularProgress size={40} color="primary" />
            </Box>
          ) : Object.keys(pageData).length === 0 ? (
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: theme.palette.text.secondary, textAlign: 'center', fontSize: '0.75rem', p: 2 }}>
              (No settings found.)
            </Typography>
          ) : (
            <Box sx={{ bgcolor: theme.palette.background.paper, p: 1, borderRadius: '4px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)', minHeight: '300px' }}>
              <Grid container spacing={2}>
                {Object.keys(pageData).map((key) => (
                  <Grid item xs={12} md={6} key={key} sx={{ px: 0.5 }}>
                    <FieldRenderer
                      label={key}
                      value={pageData[key]}
                      path={[key]}
                      onChange={handleFieldChange}
                      onOpenImageDialog={openImageDialog}
                      expandedKey={expandedKey}
                      setExpandedKey={setExpandedKey}
                      level={0}
                      searchTerm={searchTerm}
                      searchResultsPaths={searchResultsPaths}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1, borderTop: `1px solid ${harmoniousColors.divider}` }}>
          <Button onClick={onClose} sx={{ borderRadius: '6px', fontSize: '0.8rem', color: theme.palette.text.primary, px: 1.5, py: 0.4 }}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={JSON.stringify(pageData) === JSON.stringify(initialDataState)}
            onClick={handleSaveClick}
            sx={{ borderRadius: '6px', px: 1.5, fontSize: '0.8rem', py: 0.4 }}
          >
            Save Changes
          </Button>
        </DialogActions>

        {/* Confirmation Dialog (No changes) */}
        <Dialog
          open={confirmSaveOpen}
          onClose={() => setConfirmSaveOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, bgcolor: harmoniousColors.primary, color: theme.palette.primary.contrastText, py: 1, px: 2, fontSize: '1rem' }}>
            Confirm Changes
          </DialogTitle>
          <DialogContent sx={{ maxHeight: 200, overflowY: 'auto', bgcolor: theme.palette.background.paper, p: 1 }}>
            {changesList?.length ? (
              changesList.map((c, i) => {
                const pathStr = c.path.join('.');
                return (
                  <Box key={i} sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem', mb: 0.3 }}>
                      <strong>Path:</strong> {pathStr || '(root)'}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ fontSize: '0.75rem', mb: 0.3 }}>
                      <strong>Old:</strong> {JSON.stringify(c.oldVal)}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                      <strong>New:</strong> {JSON.stringify(c.newVal)}
                    </Typography>
                  </Box>
                );
              })
            ) : (
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: theme.palette.text.secondary, fontSize: '0.75rem', p: 2 }}>
                No changes.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setConfirmSaveOpen(false)} sx={{ borderRadius: '6px', fontSize: '0.75rem', color: theme.palette.text.primary, px: 1.5, py: 0.3 }}>Cancel</Button>
            <Button variant="contained" color="primary" onClick={confirmSave} sx={{ borderRadius: '6px', px: 1.5, fontSize: '0.75rem', py: 0.3 }}>Confirm Save</Button>
          </DialogActions>
        </Dialog>

        {/* Image Selector Dialog (No changes) */}
        {imageDialogOpen && (
          <ImageSelectorDialog
            open={imageDialogOpen}
            onClose={() => setImageDialogOpen(false)}
            images={images}
            loading={loadingImages}
            onSelect={handleSelectImage}
            onUploadComplete={handleUploadComplete}
            subdomain={subdomain}
          />
        )}

        <Snackbar // Snackbar (No changes)
          open={snackbarOpen}
          autoHideDuration={5000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarText}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{
            '& .MuiSnackbarContent-root': {
              bgcolor: snackbarText.includes('Error') ? 'error.main' : 'success.main',
              color: theme.palette.primary.contrastText,
              borderRadius: '4px',
              fontSize: '0.7rem',
              minWidth: 'auto',
              padding: '0.4rem 0.8rem',
            },
          }}
        />
      </Dialog>
    </ThemeProvider>
  );
}

RainbowFunEditor.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  pageSlug: PropTypes.string.isRequired,
  componentKey: PropTypes.string,
  onSave: PropTypes.func,
  subdomain: PropTypes.string.isRequired,
};

export default RainbowFunEditor;
